import React, { useState, useEffect, useMemo } from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  styled,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { ClearIcon } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import { setFilters, setDateRange, resetDateRange } from '../../redux/task/manageTasks/slice'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import { selectTasks, selectDateRange, selectFilters } from '../../redux/task/manageTasks/selector'
import { TaskFilters } from '../../redux/task/manageTasks/types'

const SearchTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
  },
})

// Filter categories displayed in the left sidebar
const FILTER_CATEGORIES = ['Operations Manager', 'Functional Area', 'Supervisor', 'Importance', 'Created Date']

// Map category label to TaskFilters key
const categoryToKey = (category: string): keyof TaskFilters => {
  switch (category) {
    case 'Importance':
      return 'importance'
    case 'Operations Manager':
      return 'operationalManager'
    case 'Supervisor':
      return 'supervisor'
    case 'Functional Area':
      return 'functionalArea'
    default:
      return 'importance'
  }
}

// Static options for importance
const IMPORTANCE_OPTIONS = ['Required', 'Optional']

const defaultFilters: TaskFilters = {
  importance: [],
  operationalManager: [],
  supervisor: [],
  functionalArea: [],
}

const TaskFilter: React.FC = () => {
  const dispatch = useAppDispatch()
  const tasks = useAppSelector(selectTasks)

  const dateRange = useAppSelector(selectDateRange)
  const reduxFilters = useAppSelector(selectFilters)

  const [open, setOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('Operations Manager')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilters, setSelectedFilters] = useState<TaskFilters>(reduxFilters)
  const [countOfSelectedFilter, setCountOfSelectedFilter] = useState(0)
  const [localStartDate, setLocalStartDate] = useState<Dayjs | null>(
    dateRange.startDate ? dayjs(dateRange.startDate) : null,
  )
  const [localEndDate, setLocalEndDate] = useState<Dayjs | null>(
    dateRange.endDate ? dayjs(dateRange.endDate) : null,
  )
  const [dateError, setDateError] = useState<string | null>(null)

  // Sync local selectedFilters state with Redux whenever the Redux filters change
  // (e.g. after navigating away and back to this page, the component remounts
  // and this effect restores the filter checkboxes from the persisted Redux state)
  useEffect(() => {
    setSelectedFilters(reduxFilters)
  }, [reduxFilters])

  // Derive unique filter options directly from the tasks' own fields
  const filterOptions = useMemo(() => {
    const operationalManagers = new Set<string>()
    const supervisors = new Set<string>()
    const functionalAreas = new Set<string>()

    for (const task of tasks) {
      ;(task.operationalManager || []).forEach((om) => om.name && operationalManagers.add(om.name))
      ;(task.supervisor || []).forEach((sv) => sv.name && supervisors.add(sv.name))
      ;(task.functionalArea || []).forEach((fa) => fa && functionalAreas.add(fa))
    }

    return {
      'Operations Manager': Array.from(operationalManagers).sort(),
      Supervisor: Array.from(supervisors).sort(),
      'Functional Area': Array.from(functionalAreas).sort(),
    }
  }, [tasks])

  // Keep count in sync with selectedFilters + active date range
  useEffect(() => {
    const count = Object.values(selectedFilters).flat().length + (hasActiveDateRange() ? 1 : 0)
    setCountOfSelectedFilter(count)
  }, [selectedFilters, dateRange])

  // Get options for the currently selected category
  const getCategoryOptions = (category: string): string[] => {
    let options: string[] = []
    switch (category) {
      case 'Importance':
        options = IMPORTANCE_OPTIONS
        break
      case 'Operations Manager':
        options = filterOptions['Operations Manager']
        break
      case 'Supervisor':
        options = filterOptions['Supervisor']
        break
      case 'Functional Area':
        options = filterOptions['Functional Area']
        break
      default:
        options = []
    }

    if (!searchQuery.trim()) return options
    return options.filter((opt) => opt.toLowerCase().includes(searchQuery.toLowerCase().trim()))
  }

  const handleApplyDateRange = () => {
    if (localStartDate && localEndDate && localStartDate.isAfter(localEndDate)) {
      setDateError('End date must be after start date')
      return
    }
    setDateError(null)
    dispatch(
      setDateRange({
        startDate: localStartDate ? localStartDate.startOf('day').toISOString() : null,
        endDate: localEndDate ? localEndDate.endOf('day').toISOString() : null,
      }),
    )
  }

  const handleResetDateRange = () => {
    setLocalStartDate(null)
    setLocalEndDate(null)
    setDateError(null)
    dispatch(resetDateRange())
  }

  const hasActiveDateRange = (): boolean => {
    if (!dateRange.startDate && !dateRange.endDate) return false
    const ytdStart = new Date(new Date().getFullYear(), 0, 1)
    const ytdEnd = new Date()
    const start = dateRange.startDate ? new Date(dateRange.startDate) : null
    const end = dateRange.endDate ? new Date(dateRange.endDate) : null
    if (!start || !end) return true
    return !(
      start.getFullYear() === ytdStart.getFullYear() &&
      start.getMonth() === ytdStart.getMonth() &&
      start.getDate() === ytdStart.getDate() &&
      end.getFullYear() === ytdEnd.getFullYear() &&
      end.getMonth() === ytdEnd.getMonth() &&
      end.getDate() === ytdEnd.getDate()
    )
  }

  const handleOpen = () => {
    // Sync local date state with Redux on open
    setLocalStartDate(dateRange.startDate ? dayjs(dateRange.startDate) : null)
    setLocalEndDate(dateRange.endDate ? dayjs(dateRange.endDate) : null)
    setDateError(null)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSearchQuery('')
  }

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category)
    setSearchQuery('')
  }

  const handleOptionToggle = (option: string) => {
    const key = categoryToKey(selectedCategory)
    const current = selectedFilters[key] || []
    const updated = current.includes(option)
      ? current.filter((item) => item !== option)
      : [...current, option]

    const newFilters: TaskFilters = {
      ...selectedFilters,
      [key]: updated,
    }
    setSelectedFilters(newFilters)
    dispatch(setFilters(newFilters))
  }

  const handleClearAll = () => {
    setSelectedFilters(defaultFilters)
    dispatch(setFilters(defaultFilters))
    setSearchQuery('')
  }

  const hasActiveFilters = () => Object.values(selectedFilters).flat().length > 0

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  return (
    <>
      <Badge badgeContent={countOfSelectedFilter} color='error'>
        <Button
          id='task-filter-button'
          color='success'
          startIcon={<FilterListIcon />}
          onClick={handleOpen}
          size='small'
          aria-haspopup='true'
          aria-label='task-filter-button'
        >
          Filter
        </Button>
      </Badge>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>Data Filters</Typography>
            <IconButton onClick={handleClose} size='small'>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box p={2}>
            <SearchTextField
              fullWidth
              placeholder='Search filter...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon color='action' />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position='end'>
                    <IconButton size='small' onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          <Box display='flex' sx={{ height: '400px' }}>
            {/* Left sidebar – Categories */}
            <Box
              sx={{
                width: { xs: '130px', sm: '200px' },
                borderRight: '1px solid #e0e0e0',
                overflowY: 'auto',
              }}
            >
              <List disablePadding>
                {FILTER_CATEGORIES.map((category) => (
                  <ListItemButton
                    key={category}
                    selected={selectedCategory === category}
                    onClick={() => handleCategoryClick(category)}
                    sx={{
                      '&.Mui-selected': { backgroundColor: '#f5f5f5' },
                      '&:hover': { backgroundColor: '#e0e0e0' },
                    }}
                  >
                    <ListItemText
                      primary={category}
                      primaryTypographyProps={{ fontSize: 14 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            {/* Right side – Options */}
            <Box sx={{ flex: 1, p: 2, overflowY: 'auto' }}>
              <Typography variant='h6' gutterBottom>
                {selectedCategory}
              </Typography>

              {selectedCategory === 'Created Date' ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box display='flex' flexDirection='column' gap={3}>
                    <Typography variant='body2' color='text.secondary'>
                      By default, tasks created Year-to-Date (Jan 1 – today) are shown. Select a custom date range below.
                    </Typography>
                    <DatePicker
                      label='Start Date'
                      value={localStartDate}
                      onChange={(newValue: any) => {
                        setLocalStartDate(newValue ? dayjs(newValue) : null)
                        setDateError(null)
                      }}
                      maxDate={localEndDate ?? undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                    <DatePicker
                      label='End Date'
                      value={localEndDate}
                      onChange={(newValue: any) => {
                        setLocalEndDate(newValue ? dayjs(newValue) : null)
                        setDateError(null)
                      }}
                      minDate={localStartDate ?? undefined}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: !!dateError,
                          helperText: dateError ?? undefined,
                        },
                      }}
                    />
                    <Box display='flex' gap={1}>
                      <Button
                        variant='contained'
                        color='primary'
                        size='small'
                        onClick={handleApplyDateRange}
                      >
                        Apply
                      </Button>
                      <Button
                        color='info'
                        size='small'
                        onClick={handleResetDateRange}
                        disabled={!hasActiveDateRange()}
                      >
                        Reset to YTD
                      </Button>
                    </Box>
                    {dateRange.startDate && (
                      <Typography variant='caption' color='text.secondary'>
                        Active filter: {dayjs(dateRange.startDate).format('MMM D, YYYY')} – {dateRange.endDate ? dayjs(dateRange.endDate).format('MMM D, YYYY') : 'today'}
                      </Typography>
                    )}
                  </Box>
                </LocalizationProvider>
              ) : (
                <List disablePadding>
                  {getCategoryOptions(selectedCategory).length === 0 ? (
                    <Typography variant='body2' color='text.secondary' sx={{ ml: 1 }}>
                      No options available
                    </Typography>
                  ) : (
                    getCategoryOptions(selectedCategory).map((option) => {
                      const key = categoryToKey(selectedCategory)
                      const isChecked = (selectedFilters[key] || []).includes(option)
                      return (
                        <ListItem
                          key={option}
                          dense
                          onClick={() => handleOptionToggle(option)}
                          sx={{
                            borderRadius: '8px',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                          }}
                        >
                          <Checkbox checked={isChecked} size='small' />
                          <ListItemText primary={option} />
                        </ListItem>
                      )
                    })
                  )}
                </List>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Button color='info' onClick={handleClearAll} disabled={!hasActiveFilters()}>
            Clear all
          </Button>
          <Button variant='contained' color='primary' onClick={handleClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TaskFilter
